import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { buildReportLifecycleEvents, track } from '../../utils/analytics';
import { markOnboardingActivated, readOnboardingState } from '../../utils/onboarding';

const hasMeaningfulContent = (report) => {
  if (report?.status === 'signed') return true;
  const body = String(report?.content || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ' ').trim();
  const patient = String(report?.patient_name || '').trim();
  return Boolean(body || patient);
};

const snapshotReports = (reports) => (reports || []).map((report) => ({
  id: report.id,
  status: report.status,
  template_id: report.template_id || null,
  has_meaningful_content: hasMeaningfulContent(report),
}));

export default function ReportActivityAnalytics() {
  const location = useLocation();
  const { reports, reportsLoaded, templates, signatures = [] } = useData();
  const { user } = useAuth();
  const previousRef = useRef(null);
  const userRef = useRef(null);
  const lastTemplateIntentRef = useRef(null);
  const firstReportEntryRef = useRef(null);

  useEffect(() => {
    if (userRef.current !== user?.id) {
      userRef.current = user?.id || null;
      previousRef.current = null;
      lastTemplateIntentRef.current = null;
      firstReportEntryRef.current = null;
    }
    if (!user?.id || !reportsLoaded) return;

    const current = snapshotReports(reports);
    if (previousRef.current === null) {
      previousRef.current = current;
      if (typeof localStorage !== 'undefined' && current.some((report) => report.has_meaningful_content)) {
        markOnboardingActivated(localStorage, user.id);
      }
      return;
    }

    for (const lifecycleEvent of buildReportLifecycleEvents(previousRef.current, current)) {
      if (lifecycleEvent.event === 'first_report_created') {
        const onboarding = readOnboardingState(typeof localStorage !== 'undefined' ? localStorage : null, user.id);
        if (onboarding.status === 'activated') continue;
        track(lifecycleEvent.event, lifecycleEvent.properties);
        if (typeof localStorage !== 'undefined' && markOnboardingActivated(localStorage, user.id)) {
          track('onboarding_activated', { activation_event: 'first_report_created' });
        }
        continue;
      }
      track(lifecycleEvent.event, lifecycleEvent.properties);
    }

    previousRef.current = current;
  }, [reports, reportsLoaded, user?.id]);

  useEffect(() => {
    if (!user?.id || !reportsLoaded || location.pathname !== '/novo-laudo') return;
    const hasExistingReport = (reports || []).some(hasMeaningfulContent);
    if (hasExistingReport) return;

    const entryKey = `${user.id}:${location.pathname}:${location.search}`;
    if (firstReportEntryRef.current === entryKey) return;
    firstReportEntryRef.current = entryKey;

    const templateId = new URLSearchParams(location.search).get('templateId');
    track('first_report_form_viewed', {
      entry_surface: templateId ? 'template_deep_link' : 'direct_editor',
      used_template_intent: Boolean(templateId),
      has_signature: signatures.length > 0,
    });

    if (signatures.length === 0) {
      track('signature_setup_required', {
        surface: 'first_report_editor',
        reason: 'emit_requires_signature',
      });
    }
  }, [location.pathname, location.search, reports, reportsLoaded, signatures.length, user?.id]);

  useEffect(() => {
    if (!user?.id || location.pathname !== '/novo-laudo') return;
    const templateId = new URLSearchParams(location.search).get('templateId');
    if (!templateId || lastTemplateIntentRef.current === templateId) return;
    const template = (templates || []).find((item) => item.id === templateId);
    if (!template) return;

    lastTemplateIntentRef.current = templateId;
    track('template_loaded_in_editor', {
      template_id: templateId,
      template_source: template.user_id === user.id ? 'owned' : 'public_catalog',
      entry_surface: 'template_deep_link',
    });
  }, [location.pathname, location.search, templates, user?.id]);

  return null;
}
